import React from "react";
import {
  Drill,
  Paintbrush2,
  Hammer,
  Layers,
  Bath,
  PlugZap2,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const IMG_LAWN =
  "https://images.unsplash.com/photo-1731168273756-e02cae42265b?auto=format&fit=crop&w=1200&q=80";
const IMG_SNOW =
  "https://images.unsplash.com/photo-1671681739893-e8d027788284?auto=format&fit=crop&w=1200&q=80";
const IMG_REPAIRS =
  "https://images.unsplash.com/photo-1634586648651-f1fb9ec10d90?auto=format&fit=crop&w=1600&q=80";
const IMG_KITCHEN =
  "https://images.unsplash.com/photo-1560185127-2d06c6d08d3d?auto=format&fit=crop&w=1200&q=80";
const IMG_FLOORING =
  "https://images.unsplash.com/photo-1523413307857-ef24c53571ae?auto=format&fit=crop&w=1200&q=80";

export const Services = () => {
  const { t } = useLanguage();
  const s = t.services.items;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 px-6 md:px-12 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#2A5A3B]">
              {t.services.overline}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-[#1C1C1C]">
              {t.services.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-muted-foreground leading-relaxed text-base">
              {t.services.subtitle}
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 auto-rows-[260px]">
          {/* Repairs & maintenance — large with image (lead service) */}
          <article
            data-testid="service-repairs-card"
            className="md:col-span-4 md:row-span-2 relative overflow-hidden border border-earth bg-white hover-lift group"
          >
            <img
              src={IMG_REPAIRS}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A231B] via-[#1A231B]/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 text-white">
              <Hammer className="h-6 w-6 text-[#E8A58B] mb-4" />
              <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
                {s.repairs.name}
              </h3>
              <p className="mt-3 max-w-md text-sm text-white/85 leading-relaxed">
                {s.repairs.desc}
              </p>
            </div>
          </article>

          {/* Kitchen & bath renovation */}
          <article
            data-testid="service-renovation-card"
            className="md:col-span-2 relative overflow-hidden border border-earth hover-lift group"
          >
            <img
              src={IMG_KITCHEN}
              alt=""
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A231B]/90 to-[#1A231B]/20" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <Bath className="h-7 w-7 text-[#E8D6C2]" />
              <div>
                <h3 className="font-display text-xl font-medium">{s.renovation.name}</h3>
                <p className="mt-3 text-sm text-white/85 leading-relaxed">
                  {s.renovation.desc}
                </p>
              </div>
            </div>
          </article>

          {/* Snow */}
          <article
            data-testid="service-snow-card"
            className="md:col-span-2 relative overflow-hidden border border-earth hover-lift group"
          >
            <img
              src={IMG_SNOW}
              alt=""
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A231B]/90 to-[#1A231B]/20" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <Paintbrush2 className="h-7 w-7 text-[#E8D6C2]" />
              <div>
                <h3 className="font-display text-xl font-medium">
                  {s.snow.name}
                </h3>
                <p className="mt-3 text-sm text-white/85 leading-relaxed">
                  {s.snow.desc}
                </p>
              </div>
            </div>
          </article>

          {/* Flooring & tiling */}
          <article
            data-testid="service-design-card"
            className="md:col-span-3 relative overflow-hidden border border-earth hover-lift group"
          >
            <img
              src={IMG_FLOORING}
              alt=""
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A231B]/90 to-[#1A231B]/20" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <Layers className="h-7 w-7 text-[#E8D6C2]" />
              <div>
                <h3 className="font-display text-xl font-medium">
                  {s.design.name}
                </h3>
                <p className="mt-3 text-sm text-white/85 leading-relaxed">
                  {s.design.desc}
                </p>
              </div>
            </div>
          </article>

          {/* Lawn care — with image */}
          <article
            data-testid="service-lawn-card"
            className="md:col-span-3 relative overflow-hidden border border-earth hover-lift group"
          >
            <img
              src={IMG_LAWN}
              alt=""
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A231B]/90 to-[#1A231B]/20" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <Drill className="h-7 w-7 text-[#E8D6C2]" />
              <div>
                <h3 className="font-display text-xl font-medium">
                  {s.lawn.name}
                </h3>
                <p className="mt-3 text-sm text-white/85 leading-relaxed">
                  {s.lawn.desc}
                </p>
              </div>
            </div>
          </article>

          {/* Cleanups */}
          <article
            data-testid="service-cleanup-card"
            className="md:col-span-6 bg-white p-8 border border-earth hover-lift flex items-center gap-6 flex-wrap"
          >
            <div className="flex items-center gap-4">
              <PlugZap2 className="h-7 w-7 text-[#C05C3F]" />
              <h3 className="font-display text-xl font-medium text-[#1C1C1C]">
                {s.cleanup.name}
              </h3>
            </div>
            <p className="flex-1 min-w-[240px] text-sm text-muted-foreground leading-relaxed">
              {s.cleanup.desc}
            </p>
            <a
              href="#quote"
              data-testid="service-cleanup-cta"
              className="text-sm uppercase tracking-[0.2em] font-semibold text-[#2A5A3B] hover:text-[#C05C3F] transition-colors"
            >
              {t.hero.cta1} →
            </a>
          </article>
        </div>
      </div>
    </section>
  );
};
